import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';

dayjs.extend(customParseFormat);
dayjs.extend(isSameOrBefore);

const LEGAL_AGE = 18;
const MAX_AGE = 120;

export const isOver18YearsOld = (date: string) => {
  const parsedDate = dayjs(date, 'YYYY-MM-DD', true);
  if (!parsedDate.isValid()) {
    return false;
  }
  if (isValidDateInTheFuture(date)) {
    return false;
  }
  const currentAgeExtractedFromDate = dayjs().diff(parsedDate, 'year');
  return currentAgeExtractedFromDate >= LEGAL_AGE;
};

export const isUnder120YearsOld = (date: string) => {
  const parsedDate = dayjs(date, 'YYYY-MM-DD', true);
  if (!parsedDate.isValid()) {
    return false;
  }
  if (isValidDateInTheFuture(date)) {
    return false;
  }
  const currentAgeExtractedFromDate = dayjs().diff(parsedDate, 'year');
  return currentAgeExtractedFromDate <= MAX_AGE;
};

export const isValidPartialDateInThePast = (date: string) => {
  const daysInPast = dayjs().diff(dayjs(date, 'YYYY-MM'), 'day');
  return daysInPast >= 0;
};

export const isAfter1900 = (date: string) => {
  const minDateThreshold = dayjs('1900-01', 'YYYY-MM');
  const inputDate = dayjs(date, 'YYYY-MM');

  return inputDate.isAfter(minDateThreshold);
};

export const isValidDateInTheFuture = (date: string | Date) => {
  const dateFormat = 'YYYY-MM-DD';
  const dateToCheck = typeof date === 'string' ? dayjs(date, dateFormat) : dayjs(date);
  return dayjs().isBefore(dateToCheck, 'day');
};

export const isValidPartialDateBefore = (date: string, beforeDate: string) => {
  const daysInPast = dayjs(beforeDate, 'YYYY-MM').diff(dayjs(date, 'YYYY-MM'), 'day');

  return daysInPast >= 0;
};

export const isValidDate = (date: string) => {
  const parsedDate = dayjs(date, 'YYYY-MM-DD', true);
  if (!parsedDate.isValid()) {
    return false;
  }
  return !isValidDateInTheFuture(date);
};

const SYDNEY_IANA = 'Australia/Sydney';
const GMT_LONG_OFFSET = /GMT([+-])(\d{1,2})(?::(\d{2}))?/;

/**
 * AEST or AEDT for Sydney at `at` (default: now), using IANA Australia/Sydney (NSW DST rules).
 * Call at render time — do not cache at module load on the server.
 */
export const getTimezoneAbbreviation = (at: Date = new Date()): 'AEST' | 'AEDT' => {
  const offsetPart = new Intl.DateTimeFormat('en', {
    timeZone: SYDNEY_IANA,
    timeZoneName: 'longOffset',
  })
    .formatToParts(at)
    .find((p) => p.type === 'timeZoneName')?.value;

  if (!offsetPart) return 'AEST';

  const match = GMT_LONG_OFFSET.exec(offsetPart);
  if (!match) return 'AEST';

  const sign = match[1] === '+' ? 1 : -1;
  const h = Number.parseInt(match[2], 10);
  const minutes = match[3] ? Number.parseInt(match[3], 10) : 0;
  const offsetHours = sign * (h + minutes / 60);

  return offsetHours > 10 ? 'AEDT' : 'AEST';
};
